import { useMemo } from 'react';
import { SearchableSelect } from './searchable-select';
import { useEmployees } from '../../services/httpServices/queries';
import type { Employee } from '../../services/httpServices/employeeService';
import { employeeDisplayName, employeeInitials } from '../../utils/employee';
import { AlertCircle } from 'lucide-react';

interface EmployeeSearchSelectProps {
  value: string;
  onChange: (employeeId: string, employee?: Employee) => void;
  label?: React.ReactNode;
  required?: boolean;
  error?: React.ReactNode;
  disabled?: boolean;
  placeholder?: string;
  /** Hide the selected-employee preview card when space is tight. */
  showPreview?: boolean;
  /** Only list employees who are eligible for payroll. */
  payrollOnly?: boolean;
}

/**
 * Searchable dropdown of active employees. Options show the employee name plus
 * code / designation / department so admins can disambiguate people quickly.
 * Selecting a value also returns the full employee so callers can prefill data.
 */
export function EmployeeSearchSelect({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  placeholder = 'Search employee by name or code…',
  showPreview = true,
  payrollOnly = false,
}: EmployeeSearchSelectProps) {
  const { data, isLoading } = useEmployees(1, 500, {
    status: 'active',
    ...(payrollOnly ? { payrollEnabled: 'true' } : {}),
  });
  const employees = useMemo(() => data?.data ?? [], [data]);

  const options = useMemo(
    () =>
      employees.map((employee) => ({
        value: employee.id,
        label: employeeDisplayName(employee),
        description: [employee.employeeCode, employee.designation?.name, employee.department?.name]
          .filter(Boolean)
          .join(' · '),
        keywords: [employee.employeeCode, employee.email, employee.mobileNumber].filter(Boolean) as string[],
      })),
    [employees]
  );

  const selected = useMemo(() => employees.find((e) => e.id === value), [employees, value]);

  return (
    <div>
      <SearchableSelect
        value={value}
        onChange={(id) => onChange(id, employees.find((e) => e.id === id))}
        options={options}
        placeholder={placeholder}
        loading={isLoading}
        disabled={disabled}
        label={
          label !== undefined ? (
            <>
              {label}
              {required && <span className="text-danger"> *</span>}
            </>
          ) : undefined
        }
        labelClassName="block text-sm font-medium text-text mb-1"
        error={
          error ? (
            <p className="flex items-center gap-1 mt-1 text-xs text-danger">
              <AlertCircle size={12} />
              {error}
            </p>
          ) : undefined
        }
      />

      {showPreview && selected && (
        <div className="mt-2 flex items-center gap-3 p-2.5 rounded-lg border border-primary/20 bg-primary/5">
          <div className="w-9 h-9 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold overflow-hidden">
            {selected.photoUrl ? (
              <img src={selected.photoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              employeeInitials(selected)
            )}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-text truncate">{employeeDisplayName(selected)}</p>
            <p className="text-xs text-text-muted truncate">
              {[selected.employeeCode, selected.designation?.name, selected.department?.name]
                .filter(Boolean)
                .join(' · ') || 'Employee'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
